import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

/**
 * Theme, for real this time.
 *
 * The bug: this provider used to do `classList.add('dark')` — a CLASS —
 * while `wordrush-design.css` declares the entire dark palette under
 * `[data-theme="dark"]` — an ATTRIBUTE. Nothing in the app ever set
 * `data-theme`, so **dark mode had never once rendered** since the design
 * system landed. It also pinned `<meta name="theme-color">` to #8b5cf6, a
 * purple left over from a dead glass-morphism era, which is what iOS
 * painted the status bar.
 *
 * Now:
 *  - `mode` is a 3-way user choice: 'system' | 'light' | 'dark',
 *    persisted to localStorage under `wr.theme`.
 *  - The RESOLVED theme ('light' | 'dark') is written to
 *    `document.documentElement.dataset.theme`, which is what the CSS reads.
 *  - `theme-color` is read back off the computed `--bg` custom property, so
 *    it can never drift from the palette again.
 *  - In 'system' mode we track `prefers-color-scheme` live.
 */

export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'wr.theme';
const LEGACY_STORAGE_KEY = 'wordrush-theme'; // 'light' | 'dark'

/* ------------------------------------------------------------------ *
 * Legacy palette objects.
 *
 * Several orphaned components (`ui/GlassCard`, `ui/AnimatedBackground`,
 * `TopNavigation`, `GameModePicker`, …) still read `theme.colors.*`. The
 * design system is CSS-custom-property-driven now; these objects exist
 * only so that dead code keeps compiling. Do not build anything new on
 * them — use the CSS tokens (`var(--ink)`, `var(--accent)`, …).
 * ------------------------------------------------------------------ */
interface Theme {
  mode: ResolvedTheme;
  colors: {
    primary: string;
    primaryHover: string;
    secondary: string;
    accent: string;
    background: string;
    backgroundSecondary: string;
    surface: string;
    surfaceHover: string;
    text: string;
    textSecondary: string;
    textMuted: string;
    success: string;
    warning: string;
    error: string;
    info: string;
    border: string;
    borderLight: string;
    glassBg: string;
    glassBlur: string;
    gradient: string;
    gradientRadial: string;
  };
  shadows: { sm: string; md: string; lg: string; xl: string };
  animations: { transition: string; spring: string };
}

const lightTheme: Theme = {
  mode: 'light',
  colors: {
    primary: '#FF5A1F',
    primaryHover: '#FF7A47',
    secondary: '#16A34A',
    accent: '#D97706',
    background: '#FAF7F2',
    backgroundSecondary: '#F4EFE6',
    surface: '#FFFFFF',
    surfaceHover: '#FBFAF7',
    text: '#16161A',
    textSecondary: '#2C2C33',
    textMuted: '#5D5A54',
    success: '#16A34A',
    warning: '#D97706',
    error: '#DC2626',
    info: '#64748B',
    border: 'rgba(22, 22, 26, 0.08)',
    borderLight: 'rgba(22, 22, 26, 0.14)',
    glassBg: 'rgba(255, 255, 255, 0.8)',
    glassBlur: 'blur(10px)',
    gradient: 'linear-gradient(180deg, #FF7A47, #FF5A1F)',
    gradientRadial: 'radial-gradient(circle at top left, #FFE8DD, #FAF7F2)',
  },
  shadows: {
    sm: '0 1px 2px rgba(22,22,26,0.05)',
    md: '0 4px 6px -1px rgba(22,22,26,0.10)',
    lg: '0 10px 15px -3px rgba(22,22,26,0.10)',
    xl: '0 20px 25px -5px rgba(22,22,26,0.10)',
  },
  animations: {
    transition: 'all 0.2s ease',
    spring: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
};

const darkTheme: Theme = {
  mode: 'dark',
  colors: {
    primary: '#FF5A1F',
    primaryHover: '#FF7A47',
    secondary: '#22C55E',
    accent: '#F59E0B',
    background: '#0A0A0C',
    backgroundSecondary: '#111114',
    surface: '#18181B',
    surfaceHover: '#1F1F23',
    text: '#F5F2EC',
    textSecondary: '#DEDBD3',
    textMuted: '#9C988F',
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#F87171',
    info: '#94A3B8',
    border: 'rgba(255, 255, 255, 0.08)',
    borderLight: 'rgba(255, 255, 255, 0.14)',
    glassBg: 'rgba(24, 24, 27, 0.8)',
    glassBlur: 'blur(10px)',
    gradient: 'linear-gradient(180deg, #FF7A47, #FF5A1F)',
    gradientRadial: 'radial-gradient(circle at top left, #26262B, #0A0A0C)',
  },
  shadows: {
    sm: '0 1px 2px rgba(0,0,0,0.4)',
    md: '0 4px 12px rgba(0,0,0,0.4)',
    lg: '0 16px 48px rgba(0,0,0,0.5)',
    xl: '0 24px 64px rgba(0,0,0,0.6)',
  },
  animations: {
    transition: 'all 0.2s ease',
    spring: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
};

/* ------------------------------------------------------------------ */

function readStoredMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'system' || saved === 'light' || saved === 'dark') return saved;
    // Migrate the old boolean-ish key. If a user had explicitly picked
    // dark under the broken implementation, honour the intent.
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy === 'dark') return 'dark';
    if (legacy === 'light') return 'light';
  } catch {
    /* private mode / storage disabled — fall through */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Push the resolved theme onto <html> and sync the iOS status-bar colour. */
function applyTheme(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // THE fix: the CSS palette keys off [data-theme], not .dark.
  root.dataset.theme = resolved;
  // Keep the class in sync too — a few legacy/Tailwind-flavoured styles
  // still key off `.dark`, and it costs nothing.
  root.classList.toggle('dark', resolved === 'dark');
  root.classList.toggle('light', resolved === 'light');
  // Let the UA style form controls, scrollbars and the like correctly.
  root.style.colorScheme = resolved;

  // Drive <meta name="theme-color"> from the *actual* --bg token rather
  // than a hand-maintained duplicate that drifts (it was a dead purple).
  const bg = getComputedStyle(root).getPropertyValue('--bg').trim();
  if (bg) {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', bg);
  }
}

interface ThemeContextType {
  /** The user's choice, including 'system'. */
  mode: ThemeMode;
  /** What's actually on screen right now. */
  resolved: ResolvedTheme;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  /** Flips between explicit light and dark (leaves 'system'). */
  toggleTheme: () => void;
  /** @deprecated legacy alias for setMode. */
  setTheme: (mode: ThemeMode) => void;
  /** @deprecated legacy palette object — use the CSS custom properties. */
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Track the OS preference for as long as we're in 'system' mode.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    // Safari < 14 only has the deprecated addListener.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const resolved: ResolvedTheme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  const toggleTheme = useCallback(() => {
    setMode(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setMode]);

  const value = useMemo<ThemeContextType>(() => ({
    mode,
    resolved,
    isDark: resolved === 'dark',
    setMode,
    toggleTheme,
    setTheme: setMode,
    theme: resolved === 'dark' ? darkTheme : lightTheme,
  }), [mode, resolved, setMode, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
